import { checklistCounts } from "../lib/checklists";
import type { ChecklistItem, JobChecklist } from "../types";
import formStyles from "./NewJobForm.module.css";
import styles from "./TasksPage.module.css";

interface TasksPageProps {
	checklists: JobChecklist[];
	onChange: (checklists: JobChecklist[]) => void;
}

/** Draft-only checklist editor; the parent form owns validation and saving. */
export function TasksPage({ checklists, onChange }: TasksPageProps) {
	const updateChecklist = (id: string, patch: Partial<JobChecklist>): void => {
		onChange(
			checklists.map((checklist) =>
				checklist.id === id ? { ...checklist, ...patch } : checklist,
			),
		);
	};
	const updateItem = (
		checklist: JobChecklist,
		id: string,
		patch: Partial<ChecklistItem>,
	): void => {
		updateChecklist(checklist.id, {
			items: checklist.items.map((item) =>
				item.id === id ? { ...item, ...patch } : item,
			),
		});
	};
	const addChecklist = (): void => {
		onChange([
			...checklists,
			{ id: crypto.randomUUID(), title: "Checklist", items: [] },
		]);
	};

	return (
		<section className={formStyles.section} aria-label="Tasks">
			<div className={styles.head}>
				<h3 className={formStyles.sectionTitle}>Checklists</h3>
				{checklists.length > 0 && (
					<button
						type="button"
						className={formStyles.cancel}
						onClick={addChecklist}
					>
						Add checklist
					</button>
				)}
			</div>

			{checklists.length === 0 ? (
				<div className={styles.empty}>
					<p className={styles.emptyText}>
						No checklists yet. Group this job’s tasks into a checklist —
						fit-off, make good, sign-off — and tick them as the work lands.
					</p>
					<button
						type="button"
						className={formStyles.cancel}
						onClick={addChecklist}
					>
						Add checklist
					</button>
				</div>
			) : (
				checklists.map((checklist, checklistIndex) => {
					const counts = checklistCounts([checklist]);
					// Empty checklists read as 0% rather than dividing by zero.
					const percent = counts.total
						? Math.round((counts.completed / counts.total) * 100)
						: 0;
					return (
						<fieldset key={checklist.id} className={styles.checklist}>
							<legend className={styles.legend}>
								Checklist {checklistIndex + 1}
							</legend>
							<div className={styles.checklistHead}>
								<input
									className={styles.title}
									aria-label={`Checklist ${checklistIndex + 1} title`}
									value={checklist.title}
									placeholder="Checklist"
									maxLength={200}
									onChange={(event) =>
										updateChecklist(checklist.id, { title: event.target.value })
									}
									onBlur={() => {
										if (!checklist.title.trim())
											updateChecklist(checklist.id, { title: "Checklist" });
									}}
								/>
								<span
									className={styles.progress}
									role="status"
									aria-live="polite"
									aria-label={`${counts.completed} of ${counts.total} items completed`}
								>
									{counts.total > 0 && (
										<span className={styles.track} aria-hidden="true">
											<span
												className={styles.fill}
												style={{ width: `${percent}%` }}
											/>
										</span>
									)}
									<span
										className={styles.count}
										data-done={
											counts.total > 0 && counts.completed === counts.total
										}
									>
										{counts.completed}/{counts.total}
									</span>
								</span>
								<button
									type="button"
									className={`${styles.remove} ${styles.removeChecklist}`}
									title={`Remove checklist ${checklistIndex + 1}`}
									aria-label={`Remove checklist ${checklistIndex + 1}`}
									onClick={() =>
										onChange(
											checklists.filter(
												(candidate) => candidate.id !== checklist.id,
											),
										)
									}
								>
									<CrossIcon />
								</button>
							</div>

							<ul className={styles.items}>
								{checklist.items.map((item, itemIndex) => (
									<li className={styles.item} key={item.id}>
										<input
											type="checkbox"
											className={styles.check}
											checked={item.completed}
											aria-label={`Complete item ${itemIndex + 1} in checklist ${checklistIndex + 1}`}
											onChange={(event) =>
												updateItem(checklist, item.id, {
													completed: event.target.checked,
												})
											}
										/>
										<input
											className={styles.text}
											data-completed={item.completed}
											aria-label={`Item ${itemIndex + 1} description in checklist ${checklistIndex + 1}`}
											placeholder="Describe the task"
											value={item.description}
											maxLength={1000}
											onChange={(event) =>
												updateItem(checklist, item.id, {
													description: event.target.value,
												})
											}
										/>
										<span className={styles.due}>
											<input
												type="date"
												className={styles.dueInput}
												data-set={item.dueOn ? "true" : "false"}
												value={item.dueOn}
												aria-label={`Optional due date for item ${itemIndex + 1} in checklist ${checklistIndex + 1}`}
												onChange={(event) =>
													updateItem(checklist, item.id, {
														dueOn: event.target.value,
													})
												}
											/>
											{/* Stands in for the UA's "dd/mm/yyyy" while the row is at
											    rest, so an unset optional date stays quiet. */}
											{!item.dueOn && (
												<span
													className={styles.duePlaceholder}
													aria-hidden="true"
												>
													Due date
												</span>
											)}
										</span>
										<button
											type="button"
											className={styles.remove}
											title="Remove item"
											aria-label={`Remove item ${itemIndex + 1} from checklist ${checklistIndex + 1}`}
											onClick={() =>
												updateChecklist(checklist.id, {
													items: checklist.items.filter(
														(candidate) => candidate.id !== item.id,
													),
												})
											}
										>
											<CrossIcon />
										</button>
									</li>
								))}
							</ul>

							<button
								type="button"
								className={styles.addItem}
								onClick={() =>
									updateChecklist(checklist.id, {
										items: [
											...checklist.items,
											{
												id: crypto.randomUUID(),
												description: "",
												completed: false,
												dueOn: "",
											},
										],
									})
								}
							>
								<span className={styles.plus} aria-hidden="true">
									+
								</span>
								Add item
							</button>
						</fieldset>
					);
				})
			)}

			<p className={formStyles.note}>
				Due dates are optional. Checklist changes are saved with the job.
			</p>
		</section>
	);
}

/** Remove affordance for rows and checklists; sized to the 28px icon button. */
function CrossIcon() {
	return (
		<svg
			width="12"
			height="12"
			viewBox="0 0 12 12"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
			aria-hidden="true"
			focusable="false"
		>
			<path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5" />
		</svg>
	);
}
